import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uploadFileToGcloud } from '@api/common';
import {
	executeImportPolygons,
	getImportPolygonStats,
	getPolygonFieldSamples,
	getPolygonSampleUrl,
	getPolygonsSystemFields,
	getPolygonUploadUrl,
	runImportPolygonStats,
	runPolygonFieldSamples,
	saveFieldsMapping,
} from '@api/importPolygons';

import { checkIsRequestAccepted, isEmpty } from '@utils/common';
import { fileSaver } from '@utils/fileSaver';
import { optionsFormattedByName } from '@utils/formatter';
import { generateHeaders } from '@utils/headersMapper';

import { IFieldsMappingDTO } from '@interfaces/import/dto/importDTO';
import { IFields, IImportStatisticsProps, ISystemFields, IUploadUrlType } from '@interfaces/import/import';

import { ESnackbarTypes } from '@enums/common';
import { EImportingDataType } from '@enums/dropzone';

import { polygonFileTypesWithoutMapping, refetchOptions } from '@constants/common';
import {
	ASSETS_LIST,
	CUSTOM_POLYGON,
	DOWNLOAD_POLYGON_SAMPLE,
	FIELD_SAMPLES,
	IMPORT_EXECUTE,
	IMPORT_STATISTIC,
	POLYGON_SYSTEM_FIELDS,
	SAVE_FIELDS_MAPPING,
	UPLOAD_URL,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';
import {
	REFETCH_STATISTICS_INTERVAL,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/constants';

export const useImportPolygons = (props: IImportStatisticsProps & IFields) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const {
		setImportIds,
		importId,
		closeModals,
		importType,
		isResultStatisticComponent,
		isFieldsMappingComponent,
	} = props;

	const [refetchIntervalImportStatistic, setRefetchIntervalImportStatistic]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);
	const [refetchIntervalFieldsSamples, setRefetchIntervalFieldsSamples]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);

	const resetIntervals = () => {
		setRefetchIntervalImportStatistic(REFETCH_STATISTICS_INTERVAL);
		setRefetchIntervalFieldsSamples(REFETCH_STATISTICS_INTERVAL);
	};

	const { mutate: downloadPolygonSampleFile } = useMutation({
		mutationKey: [DOWNLOAD_POLYGON_SAMPLE],
		mutationFn: async () => {
			try {
				const { downloadUrl } = await getPolygonSampleUrl() as any;

				if (downloadUrl) fileSaver(downloadUrl);
			} catch (err) {
				dispatch(setSnackbar({
					message: (err as Error)?.message || responseMessages.error,
					type: ESnackbarTypes.error,
				}));
			}
		},
	});

	const {
		mutate: createPolygonUploadUrl,
		data: uploadPolygonUrl,
		isLoading: isLoadingCreatePolygonUrl,
	} = useMutation<IUploadUrlType>({
		mutationKey: [UPLOAD_URL],
		mutationFn: async ({ files }: { files: Array<File> }) => {
			try {
				resetIntervals();

				const { headers, uploadUrl: url, importId } = await getPolygonUploadUrl({ fileName: files[0].name });

				await uploadFileToGcloud({
					url,
					data: files[0],
					headers: generateHeaders(headers),
				});

				if (importId) setImportIds([importId]);

				const isWithoutFieldsMapping
					= polygonFileTypesWithoutMapping.some(type => files[0].name?.includes(type));

				if (isWithoutFieldsMapping) await runImportPolygonStats(importId as string);
				else await runPolygonFieldSamples(importId);
			} catch (err) {
				if (err instanceof Error) {
					dispatch(setSnackbar({
						message: err?.message || responseMessages.error,
						type: ESnackbarTypes.error,
					}));
				}
			}
		},
	});

	const {
		data: polygonFieldsSample,
		isLoading: isLoadingPolygonFieldsSample,
		isFetching: isFetchingPolygonFieldsSample,
	} = useQuery<any>({
		queryKey: [FIELD_SAMPLES, importId],
		queryFn: () => importId && getPolygonFieldSamples(importId),
		enabled: [!!importId, isFieldsMappingComponent],
		refetchInterval: refetchIntervalFieldsSamples,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		select: (response: AxiosResponse) => {
			const fields = !isEmpty(response.data) ? optionsFormattedByName(response.data) : [];

			return { data: fields, status: response.status };
		},
		onSuccess: (data: AxiosResponse) => {
			if (checkIsRequestAccepted(data)) {
				setRefetchIntervalFieldsSamples(false);
			}
		},
	});

	const {
		data: polygonSystemFields,
		isLoading: isLoadingPolygonSystemFields,
		isFetching: isFetchingPolygonSystemFields,
	} = useQuery<Array<ISystemFields>>({
		queryKey: [POLYGON_SYSTEM_FIELDS],
		queryFn: getPolygonsSystemFields,
		...refetchOptions,
		enabled: [importType === EImportingDataType.polygon],
	});

	const {
		mutate: savePolygonFieldsMapping,
		isLoading: isSavingPolygonFieldsMapping,
	} = useMutation<{
		fieldsMapping: Array<IFieldsMappingDTO>;
		nextClick: () => void;
	}>({
		mutationKey: [SAVE_FIELDS_MAPPING],
		mutationFn: async (data: { fieldsMapping: Array<IFieldsMappingDTO>; nextClick: () => void }) => {
			await saveFieldsMapping({ id: importId as string, fieldsMapping: data.fieldsMapping });
			await runImportPolygonStats(importId as string);
			data.nextClick();
		},
		...refetchOptions,
		enabled: [!!importId, importType === EImportingDataType.polygon],
	});

	const {
		data: polygonImportStats,
		isLoading: isLoadingPolygonImportStats,
		isFetching: isFetchingPolygonImportStats,
	} = useQuery<AxiosResponse, any>({
		queryKey: [IMPORT_STATISTIC],
		queryFn: () => getImportPolygonStats(importId as string),
		select: (response) => {
			const fields = !isEmpty(response.data) ? response.data : [];

			return { data: fields, status: response.status };
		},
		refetchInterval: refetchIntervalImportStatistic,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		onSuccess: (data: AxiosResponse) => {
			if (checkIsRequestAccepted(data)) {
				setRefetchIntervalImportStatistic(false);
			}
		},
		enabled: [!!importId, importType === EImportingDataType.polygon, isResultStatisticComponent],
	});

	const {
		mutate: executePolygonImport,
		isLoading: isLoadingExecutePolygonImport,
	} = useMutation({
		mutationKey: [IMPORT_EXECUTE],
		mutationFn: () => importId && executeImportPolygons(importId),
		onSuccess: () => {
			closeModals();

			dispatch(setSnackbar({
				message: responseMessages.importAssets,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([ASSETS_LIST]),
				queryClient.invalidateQueries([CUSTOM_POLYGON]),
			];
		},
		enabled: [!!importId],
	});

	return {
		createPolygonUploadUrl,
		downloadPolygonSampleFile,
		executePolygonImport,
		isUploadingPolygonFile: isLoadingCreatePolygonUrl
			|| (isLoadingPolygonSystemFields && isFetchingPolygonSystemFields),
		isLoadingPolygonImportStats,
		polygonFieldsSample: polygonFieldsSample?.data || [],
		polygonImportStats: polygonImportStats?.data || [],
		polygonSystemFields: polygonSystemFields || [],
		savePolygonFieldsMapping,
		uploadPolygonUrl,
		isLoadingPolygonFieldsMapping: (isLoadingPolygonFieldsSample && isFetchingPolygonFieldsSample)
			|| isSavingPolygonFieldsMapping || !checkIsRequestAccepted(polygonFieldsSample),
		isLoadingPolygonResultStatistic: (isLoadingPolygonImportStats && isFetchingPolygonImportStats)
			|| isLoadingExecutePolygonImport || !checkIsRequestAccepted(polygonImportStats),
	};
};
